import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import {
  AppealRequestForwardingStatusEnum,
  Asset,
  AssetAppeal,
  AssetAppealRequestActionStatusEnum,
} from "types";
import {
  createAssetAppeal,
  updateAssetAppeal,
} from "services/assetAppealService";
import {
  updateProhibitionReport,
  updateRecoveryReport,
} from "services/assetRecoveryService";
import Select, { MultiValue } from "react-select";
import { useAuth } from "context/AuthContext";

interface SelectOption {
  value: string | number;
  label: string;
}

interface CreateAppealRequestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  data: any;
  existingData?: {
    appeal_type?: string | null;
    appeal_claim_summary?: string | null;
    admin_unit?: string | null;
    org_unit?: string | null;
    requesting_prosecutor_id?: string | null;
    requesting_prosecutor_name?: string | null;
    action_status?: string;
    asset?: Asset | null | Asset[];
  };
  onSuccess?: () => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  allAssets?: Asset[];
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
}

export const CreateAppealRequestPopup: React.FC<
  CreateAppealRequestPopupProps
> = ({
  isOpen,
  onClose,
  referralId,
  data,
  existingData,
  onSuccess,
  setActiveTab,
  setAppeal,
  allAssets,
}) => {
  const [formData, setFormData] = useState({
    appeal_type: "",
    appeal_claim_summary: "",
    admin_unit: "",
    org_unit: "",
    requesting_prosecutor_id: "",
    requesting_prosecutor_name: "",
    asset: [] as string[],
  });

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { showTooltip } = useTooltip();

  // Initialize form data when popup opens or data changes
  useEffect(() => {
    if (isOpen) {
      if (existingData) {
        // Edit mode - use existing data
        setFormData({
          appeal_type: existingData.appeal_type || "",
          appeal_claim_summary: existingData.appeal_claim_summary || "",
          admin_unit: existingData.admin_unit || "",
          org_unit: existingData.org_unit || "",
          requesting_prosecutor_id: existingData.requesting_prosecutor_id || "",
          requesting_prosecutor_name:
            existingData.requesting_prosecutor_name || "",
          asset: Array.isArray(existingData.asset)
            ? existingData.asset
                .map((a) => (a ? a.id : undefined))
                .filter(
                  (id): id is string => typeof id === "string" && id !== "",
                )
            : existingData.asset?.id
              ? [existingData.asset.id as string]
              : [],
        });
      } else if (data?.currentAssetId) {
        // Create mode - pre-select the asset from the card
        setFormData((prev) => ({
          ...prev,
          asset: [data.currentAssetId],
          appeal_type: data?.appeal_type || prev.appeal_type,
        }));
      } else {
        // Reset to empty if no data
        setFormData({
          appeal_type: data?.appeal_type || "",
          appeal_claim_summary: "",
          admin_unit: "",
          org_unit: "",
          requesting_prosecutor_id: "",
          requesting_prosecutor_name: "",
          asset: [],
        });
      }
    }
  }, [isOpen, existingData, data]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } },
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.appeal_claim_summary)
      return "Appeal Claim Summary is required.";

    return "";
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      appeal_type: "",
      appeal_claim_summary: "",
      admin_unit: "",
      org_unit: "",
      requesting_prosecutor_id: "",
      requesting_prosecutor_name: "",
      asset: [],
    });
    setUploadedFiles([]);
    onClose();
    setActiveTab?.("Appeal");
  };
  const { loginData } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const userId = loginData?.user_id || null;
    const username = loginData?.full_name || null;
    const org_unit_id = loginData?.org_unit || null;
    const admin_unit_id = loginData?.admin_unit || null;

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Check if we're in edit mode
      const isEditMode = !!existingData?.appeal_claim_summary;

      if (isEditMode) {
        const submitFormData = new FormData();

        submitFormData.append(
          "appeal_claim_summary",
          formData.appeal_claim_summary,
        );

        // Determine the action status for edit mode
        const actionStatus =
          existingData?.action_status ===
          AssetAppealRequestActionStatusEnum.APPEAL_RESPONDED
            ? AssetAppealRequestActionStatusEnum.APPEAL_UPDATED
            : AssetAppealRequestActionStatusEnum.APPEAL_REQUESTED;
        submitFormData.append("action_status", actionStatus);

        // Add documents if any
        if (uploadedFiles.length > 0) {
          uploadedFiles.forEach((file) => {
            submitFormData.append("appeal_documents", file);
          });
        }

        // Call update API for edit mode
        const response = await updateAssetAppeal(
          data?.appealId,
          submitFormData,
        );
        if (response) {
          showTooltip(t("appeal_request_updated_successfully"), "success");
          handleClose();
          setAppeal?.(response?.appeal);
          setActiveTab?.("Appeal");
        }
      } else {
        // CREATE MODE: Use createAssetAppeal and send all fields
        const submitFormData = new FormData();
        submitFormData.append("asset_referral", referralId);

        // Add all selected assets
        if (formData.asset && formData.asset.length > 0) {
          formData.asset.forEach((assetId) => {
            submitFormData.append("asset", assetId);
          });
        }

        submitFormData.append(
          "action_status",
          AssetAppealRequestActionStatusEnum.APPEAL_REQUESTED,
        );
        submitFormData.append(
          "forward_status",
          AppealRequestForwardingStatusEnum.FORWARDED_TO_COORDINATOR,
        );
        submitFormData.append(
          "requesting_prosecutor_id",
          userId?.toString() ?? "",
        );
        submitFormData.append(
          "appeal_claim_summary",
          formData.appeal_claim_summary,
        );
        submitFormData.append("requesting_prosecutor_name", username ?? "");
        submitFormData.append("org_unit", org_unit_id?.toString() ?? "");
        submitFormData.append("admin_unit", admin_unit_id?.toString() ?? "");
        submitFormData.append("appeal_type", data?.appeal_type ?? "");

        if (data.parent_appeal) {
          submitFormData.append("parent_appeal", data?.parent_appeal);
        }

        if (uploadedFiles.length > 0) {
          uploadedFiles.forEach((file) => {
            submitFormData.append("appeal_claim_documents", file);
          });
        }

        // Call create API for create mode
        const response = await createAssetAppeal(submitFormData);
        if (response) {
          // Update report appealed status only in create mode
          if (data?.appeal_type === "prohibition") {
            await updateProhibitionReport(data?.report?.id, {
              is_appealed: true,
            });
          } else if (data?.appeal_type === "asset_recovery") {
            await updateRecoveryReport(data?.report?.id, { is_appealed: true });
          }

          showTooltip(t("appeal_request_created_successfully"), "success");
          handleClose();
        }
      }

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error creating appeal request:", error);
      const errorMessage =
        error.response?.data?.message ||
        (isEditMode
          ? "Failed to update appeal request"
          : "Failed to create appeal request");
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Check if we're in edit mode for UI text
  const isEditMode = !!existingData?.appeal_claim_summary;

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Update Appeal Request" : "Create Appeal Request"}
      description={
        isEditMode ? "Update the appeal request" : "Create a new appeal request"
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col mb-4 mt-2">
          {allAssets && allAssets.length > 0 && (
            <div className="flex flex-col">
              <label className="referral-form-label">
                Select Assets to Appeal
              </label>
              <Select<SelectOption, true>
                className="asset-form-field p-5 border border-gray-300 rounded-md"
                classNamePrefix="rs"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    margin: "0",
                    color: "black",
                    minHeight: "44px",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#3b82f6"
                      : state.isFocused
                        ? "#f3f4f6"
                        : "white",
                    color: state.isSelected ? "white" : "#212121",
                    fontSize: "14px",
                    cursor: "pointer",
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#e5e7eb",
                    borderRadius: "6px",
                  }),
                  multiValueLabel: (provided) => ({
                    ...provided,
                    color: "#374151",
                    fontSize: "14px",
                  }),
                }}
                isMulti
                options={allAssets?.map((asset) => ({
                  value: asset.id as string,
                  label: asset.asset_unique_id
                    ? asset.asset_unique_id
                    : String(asset.id),
                }))}
                value={
                  formData.asset
                    ? allAssets
                        .filter((asset) =>
                          formData.asset.includes(asset.id as string),
                        )
                        .map((asset) => ({
                          value: asset.id as string,
                          label: asset.asset_unique_id
                            ? asset.asset_unique_id
                            : String(asset.id),
                        }))
                    : []
                }
                onChange={(selectedOptions: MultiValue<SelectOption>) => {
                  const selectedValues = selectedOptions.map(
                    (option) => option.value,
                  );
                  handleInputChange({
                    target: {
                      name: "asset",
                      value: selectedValues as string[],
                    },
                  });
                }}
                placeholder="Choose assets for appeal..."
                noOptionsMessage={() => "No assets available"}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Appeal Claim Summary</label>
          <textarea
            className="referral-form-field min-h-[100px] resize-vertical"
            onChange={handleInputChange}
            name="appeal_claim_summary"
            placeholder="Enter detailed appeal claim summary..."
            value={formData.appeal_claim_summary}
          />
        </div>

        <MultipleFileUploadComponent
          uploadedFiles={uploadedFiles}
          onFilesChange={setUploadedFiles}
          label="Appeal Documents"
        />

        <button
          type="submit"
          disabled={loading || formData.asset.length === 0}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading || formData.asset.length === 0
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-800"
          }`}
        >
          {loading
            ? isEditMode
              ? "Updating Appeal..."
              : "Creating Appeal..."
            : isEditMode
              ? "Update Appeal Request"
              : "Create Appeal Request"}
        </button>
      </form>
    </BasePopup>
  );
};
